import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { api, API, TOKEN_KEY } from "@/lib/api";
import { ShieldCheck, RotateCcw, Download, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const METHOD_STYLES = {
  POST: "bg-emerald-100 text-emerald-700",
  PUT: "bg-blue-100 text-blue-700",
  PATCH: "bg-amber-100 text-amber-700",
  DELETE: "bg-red-100 text-red-700",
};

export default function Admin() {
  const { user } = useAuth();
  const canExportTenant = ["admin", "org_admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [exporting, setExporting] = useState(null); // 'self' | 'tenant' | null

  const reload = () => {
    api.get("/audit-log?limit=100").then(({ data }) => {
      setRows(data.items);
      setCount(data.count);
    });
  };

  const resetDemo = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-demo");
      toast.success(`Demo data reset — ${data.risks} risks, ${data.playbooks} playbooks.`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  // GDPR / portability exports. Streamed straight to disk via a temp
  // anchor + Authorization header. We use fetch() directly (not the axios
  // `api` instance) so the response can be a Blob — axios's default JSON
  // transform would deserialize and choke on large payloads.
  const downloadJson = async (path, filename, kind) => {
    setExporting(kind);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Export downloaded.");
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    let active = true;
    api
      .get("/audit-log?limit=100")
      .then(({ data }) => {
        if (!active) return;
        setRows(data.items);
        setCount(data.count);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="admin">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="ek-eyebrow">Administration</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Audit Log</h1>
            <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
              Every state-changing request is recorded — the audit-first spine of ERIO.{" "}
              <span className="font-semibold text-ek-ink" data-testid="audit-count">
                {count}
              </span>{" "}
              entries.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => downloadJson("/me/export", `erio-sar-${user?.email || "self"}.json`, "self")}
              disabled={exporting === "self"}
              data-testid="export-self-data-button"
              className="flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-semibold text-ek-ink/70 transition-colors hover:border-ek-purple hover:text-ek-purple disabled:opacity-50"
              title="Download every PII record we hold for you (GDPR Article 15)"
            >
              <User size={15} className={exporting === "self" ? "animate-pulse" : ""} />
              {exporting === "self" ? "Exporting…" : "Export my data"}
            </button>
            {canExportTenant && (
              <button
                onClick={() => downloadJson("/admin/tenant-export", `erio-tenant-${new Date().toISOString().slice(0, 10)}.json`, "tenant")}
                disabled={exporting === "tenant"}
                data-testid="export-tenant-data-button"
                className="flex items-center gap-2 rounded-full border border-ek-purple/40 bg-ek-purple/[0.04] px-4 py-2 text-sm font-semibold text-ek-purple transition-colors hover:bg-ek-purple/10 disabled:opacity-50"
                title="Download the complete tenant data archive (Article 20 portability)"
              >
                <Download size={15} className={exporting === "tenant" ? "animate-pulse" : ""} />
                {exporting === "tenant" ? "Exporting…" : "Export tenant data"}
              </button>
            )}
            <button
              onClick={resetDemo}
              disabled={resetting}
              data-testid="reset-demo-button"
              className="flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-semibold text-ek-ink/70 transition-colors hover:border-ek-purple hover:text-ek-purple disabled:opacity-50"
              title="Reset risks, playbooks and runs to the seeded demo set"
            >
              <RotateCcw size={15} className={resetting ? "animate-spin" : ""} />
              {resetting ? "Resetting…" : "Reset demo data"}
            </button>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-md border border-black/5 bg-white">
          {loading ? (
            <div className="py-20 text-center text-sm text-ek-ink/65">Loading audit log…</div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-ek-ink/65">
              <ShieldCheck size={32} className="text-ek-purple/40" />
              <p className="text-sm">No audit entries yet.</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm" data-testid="audit-table">
              <thead className="border-b border-black/5 bg-ek-canvas/50">
                <tr className="text-xs uppercase tracking-wide text-ek-ink/65">
                  <th className="px-5 py-3 font-semibold">Time</th>
                  <th className="px-5 py-3 font-semibold">Actor role</th>
                  <th className="px-5 py-3 font-semibold">Method</th>
                  <th className="px-5 py-3 font-semibold">Route</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.audit_id}
                    data-testid="audit-row"
                    className="border-b border-black/5 last:border-0 transition-colors hover:bg-ek-purple/[0.03]"
                  >
                    <td className="whitespace-nowrap px-5 py-3 text-xs text-ek-ink/65">
                      {new Date(r.ts).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-ek-ink/70">{r.actor_role || "anonymous"}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                          METHOD_STYLES[r.method] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {r.method}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-ek-ink/70">{r.route}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`font-mono text-xs font-semibold ${
                          r.status_code >= 400 ? "text-red-600" : "text-emerald-600"
                        }`}
                      >
                        {r.status_code}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
